// Synced 2x2 grid engine.
//
// Up to PLAY_CAP cells share ONE master clock. Each cell is a single-buffered
// stitcher (SD only — 4×HD is not viable per AVA research §3). Play/pause/
// speed/seek fan out to every cell; the lead cell (first with footage)
// drives the displayed master time.
//
// Stall watchdog (LLD 13 §18-finding-4): if any non-ended, buffered, paused
// cell falls behind while peers play, the master clock re-asserts `play()`
// on it. One slow cache-miss can't freeze the whole wall.

import { useCallback, useEffect, useRef, useState } from "react";

import { clipsClient } from "@/api/clipsClient";

import type { ScopeCamera } from "./useScopeSelection";

import { withInitialState, type PlayableSegment } from "../helpers/segments";

export interface GridCell {
  /** Stable id (camera doorbot_id) — used as React key. */
  id: string;
  cam: ScopeCamera;
  segments: PlayableSegment[];
  /** True iff this cell has at least one playable segment in the loaded window. */
  hasFootage: boolean;
  /** True while the cell is fetching segments / waiting on metadata. */
  loading: boolean;
}

export interface GridEngineApi {
  cells: readonly GridCell[];
  /** Wall-clock anchor of the loaded window (or 0 when nothing loaded). */
  startMs: number;
  durMs: number;
  /** Virtual offset shared by every cell (master clock; ms). */
  virtualMs: number;
  playing: boolean;
  speed: number;
  /** Index of the "selected" cell (highlighted; default 0). */
  selectedIndex: number;

  /**
   * Mount-time binder: register the `<video>` for a cell so the engine can
   * drive it directly. Pass `null` on unmount to release.
   */
  attach(cellId: string, video: HTMLVideoElement | null): void;
  /** Replace the cell list (driven by gridSel changes). */
  setCells(cams: readonly ScopeCamera[]): void;
  /** Resolve segments for every cell at the given window. */
  loadAll(startMs: number, durMs: number): Promise<void>;
  play(): void;
  pause(): void;
  toggle(): void;
  setSpeed(s: number): void;
  /** Seek every cell to the same virtual offset. */
  seekVirtual(ms: number): void;
  /** Bump the loaded window by ±10 min; re-resolves and resumes. */
  extend(direction: -1 | 1): Promise<void>;
  /** Highlight a cell (the click handler in GridStage). */
  selectCell(index: number): void;
  /** Drop the loaded window + reset to "nothing loaded". */
  clear(): void;
}

interface CellInternal {
  id: string;
  cam: ScopeCamera;
  segments: PlayableSegment[];
  segIndex: number;
  hasFootage: boolean;
  loading: boolean;
  video: HTMLVideoElement | null;
}

const STALL_TICK_MS = 1_000;

export function useGridEngine(): GridEngineApi {
  const [cells, setCellsState] = useState<readonly GridCell[]>(() => []);
  const [startMs, setStartMs] = useState(0);
  const [durMs, setDurMs] = useState(0);
  const [virtualMs, setVirtualMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeedState] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const cellsRef = useRef<CellInternal[]>([]);
  const playingRef = useRef(false);
  const speedRef = useRef(1);
  const startMsRef = useRef(0);
  const durMsRef = useRef(0);
  const loadTokenRef = useRef(0);

  useEffect(() => { playingRef.current = playing; }, [playing]);
  useEffect(() => { speedRef.current = speed; }, [speed]);
  useEffect(() => { startMsRef.current = startMs; }, [startMs]);
  useEffect(() => { durMsRef.current = durMs; }, [durMs]);

  const publish = useCallback(() => {
    setCellsState(
      cellsRef.current.map((c) => ({
        id: c.id,
        cam: c.cam,
        segments: c.segments,
        hasFootage: c.hasFootage,
        loading: c.loading,
      })),
    );
  }, []);

  const attach = useCallback((cellId: string, video: HTMLVideoElement | null) => {
    const cell = cellsRef.current.find((c) => c.id === cellId);
    if (cell) cell.video = video;
  }, []);

  const setCells = useCallback(
    (cams: readonly ScopeCamera[]) => {
      // Preserve any cells whose camera id is unchanged (so attached <video>s
      // and seg state survive a re-order).
      const next: CellInternal[] = cams.map((cam) => {
        const prev = cellsRef.current.find((c) => c.id === cam.doorbot_id);
        if (prev) {
          prev.cam = cam;
          return prev;
        }
        return {
          id: cam.doorbot_id,
          cam,
          segments: [],
          segIndex: 0,
          hasFootage: false,
          loading: false,
          video: null,
        };
      });
      cellsRef.current = next;
      if (selectedIndex >= next.length) setSelectedIndex(0);
      publish();
    },
    [publish, selectedIndex],
  );

  const resolveCell = useCallback(
    async (cell: CellInternal, s: number, d: number): Promise<void> => {
      cell.loading = true;
      cell.segments = [];
      cell.segIndex = 0;
      publish();
      try {
        const r = await clipsClient.resolve({
          device_id: cell.id,
          start_ms: s,
          duration_ms: d,
          tier: "scan", // grid is SD-only (LLD 13 §9)
        });
        cell.segments = withInitialState(r.segments);
        cell.hasFootage = cell.segments.length > 0 && cell.segments.some((seg) => seg.playback_url !== null);
      } catch {
        cell.segments = [];
        cell.hasFootage = false;
      }
      cell.loading = false;
      const first = cell.segments[0];
      const url = first ? clipsClient.resolveMediaUrl(first.playback_url) : null;
      if (cell.video && url) {
        cell.video.src = url;
        cell.video.muted = true;
        cell.video.load();
        cell.video.onended = () => {
          if (cell.segIndex + 1 < cell.segments.length) {
            cell.segIndex++;
            const nseg = cell.segments[cell.segIndex]!;
            const nurl = clipsClient.resolveMediaUrl(nseg.playback_url);
            if (cell.video && nurl) {
              cell.video.src = nurl;
              cell.video.playbackRate = speedRef.current;
              cell.video.muted = true;
              if (playingRef.current) cell.video.play().catch(() => undefined);
            }
          }
        };
        cell.video.onerror = () => {
          // 416 inside one segment — advance the cell. Other cells keep playing.
          if (cell.segIndex + 1 < cell.segments.length) {
            cell.segIndex++;
            const nseg = cell.segments[cell.segIndex]!;
            const nurl = clipsClient.resolveMediaUrl(nseg.playback_url);
            if (cell.video && nurl) {
              cell.video.src = nurl;
              cell.video.muted = true;
              if (playingRef.current) cell.video.play().catch(() => undefined);
            }
          }
        };
      }
      publish();
    },
    [publish],
  );

  const loadAll = useCallback(
    async (s: number, d: number): Promise<void> => {
      const myToken = ++loadTokenRef.current;
      setStartMs(s);
      setDurMs(d);
      setVirtualMs(0);
      startMsRef.current = s;
      durMsRef.current = d;
      const targets = cellsRef.current;
      await Promise.all(targets.map((c) => resolveCell(c, s, d)));
      if (myToken !== loadTokenRef.current) return;
      // Auto-play after load — same behavior as the prototype.
      playingRef.current = true;
      setPlaying(true);
      for (const cell of cellsRef.current) {
        if (cell.video && cell.hasFootage) {
          cell.video.muted = true;
          cell.video.playbackRate = speedRef.current;
          cell.video.play().catch(() => undefined);
        }
      }
    },
    [resolveCell],
  );

  /** Master-clock tick: read elapsed from the lead cell, fan it out, watchdog. */
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!playingRef.current) return;
      const lead = cellsRef.current.find((c) => c.hasFootage && c.video);
      if (!lead || !lead.video) return;
      let elapsed = 0;
      for (let k = 0; k < lead.segIndex; k++) elapsed += lead.segments[k]!.duration_ms;
      if (!Number.isNaN(lead.video.currentTime)) elapsed += lead.video.currentTime * 1000;
      const v = Math.min(elapsed, durMsRef.current);
      setVirtualMs(v);
      // Stall watchdog: re-assert play() on any non-ended buffered paused cell.
      for (const cell of cellsRef.current) {
        if (!cell.video || !cell.hasFootage) continue;
        if (cell.video.ended) continue;
        if (cell.video.paused && cell.video.readyState >= 3) {
          cell.video.play().catch(() => undefined);
        }
      }
      if (v >= durMsRef.current - 50) {
        playingRef.current = false;
        setPlaying(false);
        for (const cell of cellsRef.current) {
          if (cell.video) cell.video.pause();
        }
      }
    }, STALL_TICK_MS / 5);
    return () => window.clearInterval(id);
  }, []);

  const play = useCallback(() => {
    playingRef.current = true;
    setPlaying(true);
    for (const cell of cellsRef.current) {
      if (cell.video && cell.hasFootage) {
        cell.video.playbackRate = speedRef.current;
        cell.video.muted = true;
        cell.video.play().catch(() => undefined);
      }
    }
  }, []);

  const pause = useCallback(() => {
    playingRef.current = false;
    setPlaying(false);
    for (const cell of cellsRef.current) {
      if (cell.video) cell.video.pause();
    }
  }, []);

  const toggle = useCallback(() => {
    if (playingRef.current) pause();
    else play();
  }, [pause, play]);

  const setSpeed = useCallback((s: number) => {
    speedRef.current = s;
    setSpeedState(s);
    for (const cell of cellsRef.current) {
      if (cell.video) {
        cell.video.playbackRate = s;
        cell.video.muted = true;
      }
    }
  }, []);

  const seekVirtual = useCallback((ms: number) => {
    const v = Math.max(0, Math.min(durMsRef.current, ms));
    setVirtualMs(v);
    for (const cell of cellsRef.current) {
      if (!cell.hasFootage || cell.segments.length === 0) continue;
      let acc = 0;
      let i = 0;
      let offset = 0;
      for (; i < cell.segments.length; i++) {
        const segDur = cell.segments[i]!.duration_ms;
        if (v < acc + segDur || i === cell.segments.length - 1) {
          offset = Math.max(0, Math.min(segDur, v - acc));
          break;
        }
        acc += segDur;
      }
      cell.segIndex = i;
      const seg = cell.segments[i]!;
      const url = clipsClient.resolveMediaUrl(seg.playback_url);
      if (cell.video && url) {
        if (cell.video.getAttribute("src") !== url) cell.video.src = url;
        const apply = (): void => {
          try {
            if (cell.video) {
              cell.video.currentTime = Math.min(
                offset / 1000,
                cell.video.duration || offset / 1000,
              );
            }
          } catch {
            // noop
          }
        };
        if (cell.video.readyState >= 1) apply();
        else cell.video.addEventListener("loadedmetadata", apply, { once: true });
        cell.video.playbackRate = speedRef.current;
        cell.video.muted = true;
        if (playingRef.current) cell.video.play().catch(() => undefined);
      }
    }
  }, []);

  const extend = useCallback(
    async (direction: -1 | 1) => {
      let s = startMsRef.current;
      let d = durMsRef.current;
      if (direction < 0) {
        s -= 10 * 60_000;
        d += 10 * 60_000;
      } else {
        d += 10 * 60_000;
      }
      await loadAll(s, d);
    },
    [loadAll],
  );

  const selectCell = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  const clear = useCallback(() => {
    setStartMs(0);
    setDurMs(0);
    setVirtualMs(0);
    setPlaying(false);
    playingRef.current = false;
    for (const cell of cellsRef.current) {
      if (cell.video) {
        cell.video.pause();
        cell.video.removeAttribute("src");
        cell.video.load();
      }
      cell.segments = [];
      cell.hasFootage = false;
    }
    publish();
  }, [publish]);

  return {
    cells,
    startMs,
    durMs,
    virtualMs,
    playing,
    speed,
    selectedIndex,
    attach,
    setCells,
    loadAll,
    play,
    pause,
    toggle,
    setSpeed,
    seekVirtual,
    extend,
    selectCell,
    clear,
  };
}
